import { ClinicAuthError } from "@auth/clinicAuthClient";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MessageRecorder } from "@phone/MessageRecorder";
import {
  ALLOWED_AUDIO_TYPES,
  AUDIO_ACCEPT,
  AUDIO_MAX_BYTES,
  MESSAGE_TEXT_MAX_CHARS,
  phoneSetupClient,
  type PhoneMessageConfig,
  type PhoneMessagePurpose,
} from "@phone/phoneSetupClient";
import {
  useUpdatePhoneMessage,
  useUploadPhoneMessageAudio,
} from "@phone/phoneSetupQueries";
import {
  Button,
  cn,
  ConfirmDialog,
  FieldError,
  SegmentedControl,
  Textarea,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { useState } from "react";

type MessageMode = PhoneMessageConfig["mode"];

const MODE_OPTIONS: { value: MessageMode; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "text", label: "Custom text" },
  { value: "audio", label: "Uploaded audio" },
];

const ALL_MODES: MessageMode[] = ["default", "text", "audio"];

/** The visible face for a hidden `<input type="file">`; `peer-focus-visible` keeps keyboard focus visible. */
const pickerFace = cn(
  "inline-flex items-center rounded-paper border border-rule bg-card px-3.5 py-1.5",
  "font-body text-sm font-medium text-ink",
  "transition duration-150 ease-paper motion-reduce:transition-none",
  "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent",
);

const isAllowedAudio = (file: File): boolean =>
  ALLOWED_AUDIO_TYPES.includes(file.type) || /\.(wav|mp3)$/i.test(file.name);

/** Mirrors brian's upload rules (modules/phone/messages.py). */
export const refuseAudioFile = (file: File): string | null => {
  if (!isAllowedAudio(file)) return "That file isn't wav or mp3.";
  if (file.size > AUDIO_MAX_BYTES) return "That file is over 5 MB.";
  return null;
};

/** 502: the provider refused the media push; nothing was replaced. */
export const uploadErrorNote = (error: unknown): string =>
  error instanceof ClinicAuthError && error.status === 502
    ? "The phone provider wouldn't take that audio. Nothing changed — try again."
    : "Couldn't upload that audio. Try again.";

export const MessageAudioPreview = ({
  purpose,
  testIdPrefix,
}: {
  purpose: PhoneMessagePurpose;
  testIdPrefix: string;
}) => {
  const [source, setSource] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [failed, setFailed] = useState(false);

  // The presigned URL is short-lived, so every press fetches a fresh one.
  const open = async () => {
    setFetching(true);
    setFailed(false);
    setSource(null);
    try {
      const audio = await phoneSetupClient.getMessageAudioUrl(purpose);
      setSource(audio.downloadUrl);
    } catch {
      setFailed(true);
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Button
          variant="outline"
          size="sm"
          busy={fetching}
          onClick={() => void open()}
          data-testid={`${testIdPrefix}-audio-play`}
        >
          <FontAwesomeIcon icon={faPlay} aria-hidden="true" />
          {source ? "Reload audio" : "Play audio"}
        </Button>
      </div>
      {/* `data-dd-privacy="hidden"`: replay's `mask` doesn't censor `src` on
          media elements, so the presigned URL would land in snapshots.
          `onError`: with `preload="none"` a stale URL first fails on play. */}
      {source && (
        <audio
          controls
          preload="none"
          src={source}
          data-dd-privacy="hidden"
          onError={() => {
            setSource(null);
            setFailed(true);
          }}
          className="w-full"
          data-testid={`${testIdPrefix}-audio-player`}
        />
      )}
      {failed && (
        <FieldError data-testid={`${testIdPrefix}-audio-error`}>
          That audio wouldn't load. Try again in a moment.
        </FieldError>
      )}
    </div>
  );
};

/** The input clears itself so the same file can be picked again after a failure. */
export const AudioFilePicker = ({
  label,
  busy,
  onPick,
  testIdPrefix,
}: {
  label: string;
  busy: boolean;
  onPick: (file: File) => void;
  testIdPrefix: string;
}) => (
  <label
    className={cn("self-start", busy ? "cursor-not-allowed" : "cursor-pointer")}
  >
    <input
      type="file"
      accept={AUDIO_ACCEPT}
      disabled={busy}
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onPick(file);
        e.target.value = "";
      }}
      className="peer sr-only"
      data-testid={`${testIdPrefix}-audio-input`}
    />
    <span
      className={cn(
        pickerFace,
        busy
          ? "bg-recess text-faint"
          : "hover:-translate-y-px hover:shadow-lift-lg",
      )}
    >
      {label}
    </span>
  </label>
);

/**
 * Audio mode is entered by the upload itself, so Save covers only the text
 * modes. The dialog and drafts stay mounted while `controlsHidden`, so a
 * refetch that flips the switch cannot drop an open dialog.
 */
export const MessageEditor = ({
  purpose,
  testIdPrefix,
  controlLabel,
  hears,
  config,
  modes = ALL_MODES,
  maxRecordSeconds,
  hideEffective = false,
  controlsHidden = false,
}: {
  purpose: PhoneMessagePurpose;
  testIdPrefix: string;
  controlLabel: string;
  /** The confirm dialog's lead-in. */
  hears: string;
  config: PhoneMessageConfig;
  modes?: MessageMode[];
  maxRecordSeconds?: number;
  /** The owner sums the message up itself. */
  hideEffective?: boolean;
  /** The message is switched off (`MessageCard`'s `spoken`). */
  controlsHidden?: boolean;
}) => {
  const updateMessage = useUpdatePhoneMessage();
  const uploadAudio = useUploadPhoneMessageAudio();
  const [modeDraft, setModeDraft] = useState<MessageMode | null>(null);
  const [textDraft, setTextDraft] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<"save" | "reset" | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const offersDefault = modes.includes("default");
  // A saved mode not offered here (an unset hold message) lands on the first offered one.
  const savedMode = modes.includes(config.mode) ? config.mode : modes[0];
  const mode = modeDraft ?? savedMode;
  const currentText = config.customText ?? "";
  const text = textDraft ?? currentText;
  const trimmed = text.trim();

  const dirty =
    mode !== config.mode || (mode === "text" && trimmed !== currentText.trim());
  const savable =
    dirty && (mode === "default" || (mode === "text" && trimmed !== ""));

  const close = () => setConfirming(null);
  const apply = (input: Parameters<typeof updateMessage.mutate>[0]["input"]) =>
    updateMessage.mutate(
      { purpose, input },
      {
        onSuccess: () => {
          setModeDraft(null);
          setTextDraft(null);
        },
        onSettled: close,
      },
    );

  const handleFile = async (file: File): Promise<boolean> => {
    const refusal = refuseAudioFile(file);
    setRefused(refusal);
    if (refusal) return false;
    try {
      await uploadAudio.mutateAsync({ purpose, file });
      setModeDraft(null);
      setTextDraft(null);
      return true;
    } catch {
      return false;
    }
  };

  const picking = uploadAudio.isPending;
  const options = MODE_OPTIONS.filter((o) => modes.includes(o.value));

  return (
    <>
      {!controlsHidden && (
        <>
          <SegmentedControl
            aria-label={controlLabel}
            options={options}
            value={mode}
            onValueChange={(value) => setModeDraft(value)}
            data-testid={`${testIdPrefix}-mode`}
          />

          {mode === "default" && (
            <p
              className="text-sm text-annotation"
              data-testid={`${testIdPrefix}-default-text`}
            >
              The default:{" "}
              <span className="text-ink-body">“{config.defaultText}”</span>
            </p>
          )}

          {mode === "text" && (
            <Textarea
              label="Message"
              value={text}
              onChange={(e) => setTextDraft(e.target.value)}
              minRows={3}
              maxLength={MESSAGE_TEXT_MAX_CHARS}
              placeholder={config.defaultText ?? undefined}
              hint={`${text.length}/${MESSAGE_TEXT_MAX_CHARS}`}
              data-testid={`${testIdPrefix}-text`}
            />
          )}

          {mode === "audio" && (
            <div className="flex flex-col gap-3">
              {config.audio ? (
                <>
                  <p
                    className="text-sm text-ink-body"
                    data-testid={`${testIdPrefix}-audio-meta`}
                  >
                    <span className="font-mono">{config.audio.filename}</span>{" "}
                    <span className="text-annotation">
                      · uploaded {formatDateTime(config.audio.uploadedAt)}
                    </span>
                  </p>
                  <MessageAudioPreview
                    purpose={purpose}
                    testIdPrefix={testIdPrefix}
                  />
                </>
              ) : (
                <p
                  className="text-sm text-annotation"
                  data-testid={`${testIdPrefix}-audio-none`}
                >
                  No audio yet — choose a wav or mp3 file (5 MB max), or record
                  one here.{" "}
                  {config.mode === "unset"
                    ? "Until then, this message is skipped."
                    : "Until then, callers keep hearing the current message."}
                </p>
              )}
              <AudioFilePicker
                label={
                  picking
                    ? "Uploading"
                    : config.audio
                      ? "Replace audio"
                      : "Upload audio"
                }
                busy={picking}
                onPick={(file) => void handleFile(file)}
                testIdPrefix={testIdPrefix}
              />
              <MessageRecorder
                testIdPrefix={testIdPrefix}
                busy={picking}
                onRecorded={handleFile}
                maxSeconds={maxRecordSeconds}
              />
              {(refused || uploadAudio.isError) && (
                <FieldError data-testid={`${testIdPrefix}-upload-error`}>
                  {refused ?? uploadErrorNote(uploadAudio.error)}
                </FieldError>
              )}
            </div>
          )}

          {!hideEffective &&
            (config.effectiveText !== null ? (
              <p
                className="text-sm text-annotation"
                data-testid={`${testIdPrefix}-effective`}
              >
                Callers currently hear:{" "}
                <span className="text-ink-body">“{config.effectiveText}”</span>
              </p>
            ) : (
              <p
                className="text-sm text-annotation"
                data-testid={`${testIdPrefix}-effective`}
              >
                Callers currently hear your uploaded audio.
              </p>
            ))}

          <div className="flex justify-end gap-2">
            {offersDefault && config.mode !== "default" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirming("reset")}
                data-testid={`${testIdPrefix}-reset`}
              >
                Reset to default
              </Button>
            )}
            {mode !== "audio" && (
              <Button
                size="sm"
                disabled={!savable}
                onClick={() => setConfirming("save")}
                data-testid={`${testIdPrefix}-save`}
              >
                Save
              </Button>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirming !== null}
        data-testid={
          confirming !== null ? `${testIdPrefix}-confirm` : undefined
        }
        title={
          confirming === "reset"
            ? "Reset to the default message?"
            : "Change the message?"
        }
        confirmLabel={confirming === "reset" ? "Reset message" : "Save message"}
        cancelLabel="Cancel"
        busy={updateMessage.isPending}
        onCancel={close}
        onConfirm={() =>
          apply(
            confirming === "reset" || mode === "default"
              ? { mode: "default" }
              : { mode: "text", text: trimmed },
          )
        }
        confirmTestId={`${testIdPrefix}-confirm-confirm`}
        cancelTestId={`${testIdPrefix}-confirm-cancel`}
      >
        <p className="text-sm text-ink-body">
          {confirming === "reset" || mode === "default"
            ? `${hears} the default message again:`
            : `${hears}:`}
        </p>
        <p className="mt-2 text-sm text-ink">
          “
          {confirming === "reset" || mode === "default"
            ? config.defaultText
            : trimmed}
          ”
        </p>
      </ConfirmDialog>
    </>
  );
};
